import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useTranslations } from "use-intl";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import colors from "tailwindcss/colors";
import {
  WorkOrderRegionalCount,
  workOrderRegionalCountsQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/WorkOrderStatusCountQuery";
import { StatBlock } from "@/app/Dashboard/Components/Blocks/types";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { formatStatistic } from "@/lib/Formatters/formatStatistic";
import { TrendIndicator } from "@/app/Dashboard/Components/Blocks/TrendIndicator";
import { adminDashboardStatsQuery } from "@/app/Dashboard/GraphQL/adminDashboardStatsQuery";

export function WorkOrderStatBlock({ start, end, comparisonMode }: StatBlock) {
  const t = useTranslations();
  const { data } = useSuspenseGqlQuery(workOrderRegionalCountsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
  });

  const { data: adminData } = useSuspenseGqlQuery(adminDashboardStatsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
    comparisonMode,
  });

  const regionalCounts = data.workOrderRegionalCounts;

  const wonWorkOrders = regionalCounts.reduce((sum, r) => sum + r.won_count, 0);

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium">
              {t("dashboard.blocks.workOrders.title")}
            </CardTitle>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <div className="text-3xl font-bold">
              {formatStatistic(wonWorkOrders)}
            </div>
            <TrendIndicator
              trend={adminData.adminDashboardStats.work_orders_trend}
              currentValue={wonWorkOrders}
              startDate={start}
              endDate={end}
              comparisonMode={comparisonMode}
              formatValue={formatStatistic}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-auto pb-6">
        <WorkOrderRegionStackedBarChart data={regionalCounts} />
      </CardContent>
    </Card>
  );
}

function WorkOrderRegionStackedBarChart({
  data,
}: {
  data: WorkOrderRegionalCount[];
}) {
  const tCity = useTranslations("city");
  const tDashboard = useTranslations("dashboard");

  const chartConfig = {
    cancelled_count: {
      label: tDashboard("chartLabels.cancelled"),
      color: colors["red"]["500"],
    },
    pending_count: {
      label: tDashboard("chartLabels.pending"),
      color: colors["yellow"]["500"],
    },
    won_count: {
      label: tDashboard("chartLabels.won"),
      color: colors["green"]["500"],
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="h-[200px] w-full">
      <BarChart data={data}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="region"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          fontSize={12}
        />
        <YAxis tickLine={false} axisLine={false} fontSize={12} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(value) => tCity(`region.${value}`)}
            />
          }
        />
        <Bar
          dataKey="won_count"
          stackId="a"
          fill="var(--color-won_count)"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="pending_count"
          stackId="a"
          fill="var(--color-pending_count)"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="cancelled_count"
          stackId="a"
          fill="var(--color-cancelled_count)"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}
